import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Box,
  Stack,
  Alert,
  Snackbar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const contactImages = [
  { src: '/assets/sky-targets/m42.jpg', label: 'Orion Nebula' },
  { src: '/assets/sky-targets/m45.jpg', label: 'Pleiades' },
  { src: '/assets/sky-targets/ngc7000.jpg', label: 'North America Nebula' }
];

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [captcha, setCaptcha] = useState({ prompt: '', image: '', token: '', answer: '', loading: true });
  const [formStartedAt, setFormStartedAt] = useState(Math.floor(Date.now() / 1000));
  const [submitting, setSubmitting] = useState(false);
  const [proof, setProof] = useState({ focusCount: 0, changeCount: 0, keyCount: 0, pointerCount: 0 });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const loadCaptcha = async () => {
    setCaptcha((prev) => ({ ...prev, loading: true, answer: '' }));
    try {
      const response = await fetch('/api/contact/challenge', { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error('Failed to load anti-bot check');
      const data = await response.json();
      setCaptcha({ prompt: data.prompt || 'Enter the characters shown', image: data.image || '', token: data.token, answer: '', loading: false });
      setProof({ focusCount: 0, changeCount: 0, keyCount: 0, pointerCount: 0 });
      setFormStartedAt(Math.floor(Date.now() / 1000));
    } catch (error) {
      setCaptcha({ prompt: 'Anti-bot check unavailable', image: '', token: '', answer: '', loading: false });
      setSnackbarMessage('Could not load the anti-bot check. Please refresh and try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const trackProof = (field) => {
    setProof((prev) => ({ ...prev, [field]: Math.min((prev[field] || 0) + 1, 99) }));
  };

  const handleChange = (e) => {
    trackProof('changeCount');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          captchaAnswer: captcha.answer,
          captchaToken: captcha.token,
          formStartedAt,
          website: formData.website || '',
          confirmEmail: formData.confirmEmail || '',
          proof
        }),
      });

      if (response.ok) {
        setSnackbarMessage('Message sent successfully!');
        setSnackbarSeverity('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          website: '',
          confirmEmail: ''
        });
        await loadCaptcha();
      } else {
        const data = await response.json().catch(() => ({}));
        setSnackbarMessage(data.message || 'Failed to send message. Please try again.');
        await loadCaptcha();
        setSnackbarSeverity('error');
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setSnackbarSeverity('error');
      await loadCaptcha();
    } finally {
      setSubmitting(false);
    }
    setOpenSnackbar(true);
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: 'calc(100vh - 180px)', py: { xs: 2.2, md: 4.2 } }}>
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 18% 12%, rgba(245,151,79,0.20), transparent 26%),
            radial-gradient(circle at 78% 18%, rgba(114,240,255,0.12), transparent 28%),
            linear-gradient(180deg, rgba(5,7,17,0.18), rgba(5,7,17,0.92)),
            url('/assets/sky-targets/ngc7000.jpg') center/cover no-repeat
          `,
          opacity: 0.38,
          filter: 'saturate(1.08) contrast(1.05)'
        }}
      />
      <Box aria-hidden="true" sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(5,7,17,0.96) 0%, rgba(5,7,17,0.80) 42%, rgba(5,7,17,0.92) 100%)' }} />
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
      <Grid container spacing={{ xs: 2.2, md: 3.2 }} alignItems="stretch">
        <Grid item xs={12} md={5.2}>
          <Stack spacing={{ xs: 2, md: 2.35 }} sx={{ height: '100%', justifyContent: 'center' }}>
            <Box>
              <Typography variant="overline" sx={{ color: 'primary.light', letterSpacing: 2.4, fontWeight: 900 }}>
                Astro Vino contact
              </Typography>
              <Typography variant="h3" component="h1" sx={{ fontSize: { xs: '2.35rem', md: '3.2rem' }, lineHeight: 1.04, fontWeight: 900, letterSpacing: '-0.045em', mb: 1.4 }}>
                Let’s talk under clear skies
              </Typography>
              <Typography variant="body1" sx={{ maxWidth: 620, color: 'rgba(255,255,255,0.82)', fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.65 }}>
                Questions about astrophotography, collaborations, prints, captures, equipment, or the Astro Vino tools? Send a note and I’ll get back to you as soon as possible.
              </Typography>
            </Box>

            <Paper sx={{ overflow: 'hidden', borderRadius: 3, bgcolor: 'rgba(9,12,24,0.72)', border: '1px solid rgba(245,151,79,0.28)', boxShadow: '0 24px 80px rgba(0,0,0,0.42)', backdropFilter: 'blur(14px)' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.2fr 0.8fr' }, minHeight: { xs: 230, md: 330 } }}>
                <Box sx={{ position: 'relative', minHeight: { xs: 230, md: 330 }, overflow: 'hidden' }}>
                  <Box component="img" src={contactImages[0].src} alt="Astrophotography capture" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'saturate(1.08) contrast(1.04)' }} />
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.82))' }} />
                  <Box sx={{ position: 'absolute', left: 14, right: 14, bottom: 12 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '0.95rem', md: '1.08rem' } }}>Astrophotography from Northumberland</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.74)' }}>Real sky targets, planning tools and capture notes.</Typography>
                  </Box>
                </Box>
                <Stack spacing={1} sx={{ p: { xs: 1, sm: 1.2 }, minWidth: 0 }}>
                  {contactImages.slice(1).map((image) => (
                    <Box key={image.src} sx={{ position: 'relative', flex: 1, minHeight: 0, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}>
                      <Box component="img" src={image.src} alt={image.label} sx={{ width: '100%', height: '100%', minHeight: { xs: 104, md: 145 }, objectFit: 'cover', display: 'block' }} />
                      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.70))' }} />
                      <Typography variant="caption" sx={{ position: 'absolute', left: 10, bottom: 8, color: '#fff', fontWeight: 800 }}>{image.label}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Paper>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, '& > *': { borderColor: 'rgba(245,151,79,0.32)', color: 'rgba(255,255,255,0.82)' } }}>
              <Box sx={{ px: 1.4, py: 0.8, borderRadius: 999, border: '1px solid rgba(245,151,79,0.32)', bgcolor: 'rgba(245,151,79,0.08)', fontSize: '0.78rem', fontWeight: 800 }}>Collaboration</Box>
              <Box sx={{ px: 1.4, py: 0.8, borderRadius: 999, border: '1px solid rgba(245,151,79,0.32)', bgcolor: 'rgba(245,151,79,0.08)', fontSize: '0.78rem', fontWeight: 800 }}>Prints & images</Box>
              <Box sx={{ px: 1.4, py: 0.8, borderRadius: 999, border: '1px solid rgba(245,151,79,0.32)', bgcolor: 'rgba(245,151,79,0.08)', fontSize: '0.78rem', fontWeight: 800 }}>Observatory tools</Box>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6.8}>
          <MotionPaper 
            elevation={3} 
            component="form"
            onSubmit={handleSubmit}
            onFocus={() => trackProof('focusCount')}
            onKeyDown={() => trackProof('keyCount')}
            onPointerMove={() => trackProof('pointerCount')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{ 
              p: { xs: 2.2, sm: 3, md: 3.6 },
              bgcolor: 'rgba(24,27,36,0.88)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 28px 90px rgba(0,0,0,0.44)',
              backdropFilter: 'blur(16px)'
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Message"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website || ''}
                  onChange={handleChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Confirm Email"
                  name="confirmEmail"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.confirmEmail || ''}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  Secure anti-bot check: {captcha.loading ? 'Loading…' : captcha.prompt}
                </Alert>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                  <Box
                    component="img"
                    src={captcha.image}
                    alt="Anti-bot verification code"
                    sx={{
                      width: { xs: '100%', sm: 260 },
                      maxWidth: '100%',
                      borderRadius: 2,
                      border: '1px solid rgba(245,151,79,0.45)',
                      background: '#080910'
                    }}
                  />
                  <TextField
                    required
                    fullWidth
                    label="Verification code"
                    name="captchaAnswer"
                    value={captcha.answer}
                    onChange={(e) => {
                      trackProof('changeCount');
                      setCaptcha((prev) => ({ ...prev, answer: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5) }));
                    }}
                    variant="outlined"
                    inputProps={{ autoComplete: 'off', spellCheck: 'false' }}
                    disabled={captcha.loading || !captcha.token}
                    helperText="Refresh the page if the code is hard to read."
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  endIcon={<SendIcon />}
                  disabled={submitting || captcha.loading || !captcha.token || !captcha.answer}
                >
                  {submitting ? 'Sending…' : 'Send Message'}
                </Button>
              </Grid>
            </Grid>
          </MotionPaper>
        </Grid>
      </Grid>
      </Container>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Contact;
